/* global Excel, Word */

import type { InsertPosition } from "@/types";
import { isWord } from "@/services/platform";

/**
 * Formatting options for inserted content
 */
export interface TextFormatting {
  fontName?: string;
  fontSize?: number;
  fontColor?: string;
}

/**
 * Position configuration mapping for Excel
 * Maps position type to how many cells/columns to offset from selection
 */
const EXCEL_POSITION_CONFIG: Record<string, { type: "same" } | { type: "cells"; value: number } | { type: "column"; value: "left" | "right" }> = {
  "same-cell": { type: "same" },
  "1-cell-below": { type: "cells", value: 1 },
  "2-cells-below": { type: "cells", value: 2 },
  "3-cells-below": { type: "cells", value: 3 },
  "column-left": { type: "column", value: "left" },
  "column-right": { type: "column", value: "right" },
};

/**
 * Position configuration mapping for Word
 * Maps position type to how many lines to offset from selection
 */
const WORD_POSITION_CONFIG: Record<string, { type: "same" } | { type: "lines"; value: number }> = {
  "same-line": { type: "same" },
  "1-line-below": { type: "lines", value: 1 },
  "2-lines-below": { type: "lines", value: 2 },
  "3-lines-below": { type: "lines", value: 3 },
  "4-lines-below": { type: "lines", value: 4 },
};

/**
 * Get the appropriate position config based on current platform
 */
function getPositionConfig(position: InsertPosition) {
  if (isWord()) {
    return WORD_POSITION_CONFIG[position];
  }
  return EXCEL_POSITION_CONFIG[position];
}

/**
 * Apply formatting to a cell range
 */
function applyFormatting(range: Excel.Range, formatting?: TextFormatting): void {
  if (!formatting) return;
  if (formatting.fontName) range.format.font.name = formatting.fontName;
  if (formatting.fontSize) range.format.font.size = formatting.fontSize;
  if (formatting.fontColor) range.format.font.color = formatting.fontColor;
}

/**
 * Insert text at same cell
 */
function insertAtSameCell(
  range: Excel.Range,
  text: string,
  formatting?: TextFormatting
): void {
  range.values = [[text]];
  applyFormatting(range, formatting);
}

/**
 * Insert text below selection by cell count
 */
function insertBelowSelection(
  context: Excel.RequestContext,
  selection: Excel.Range,
  text: string,
  cellsBelow: number,
  formatting?: TextFormatting
): void {
  selection.load(["rowIndex", "columnIndex", "rowCount"]);
  
  const targetRowIndex = selection.rowIndex + selection.rowCount + cellsBelow - 1;
  const targetColumnIndex = selection.columnIndex;
  
  const targetCell = context.workbook.worksheets.getActiveWorksheet()
    .getCell(targetRowIndex, targetColumnIndex);
  
  targetCell.values = [[text]];
  applyFormatting(targetCell, formatting);
}

/**
 * Insert text in adjacent column
 */
async function insertInColumn(
  context: Excel.RequestContext,
  selection: Excel.Range,
  text: string,
  side: "left" | "right",
  formatting?: TextFormatting
): Promise<void> {
  selection.load(["columnIndex", "rowIndex"]);
  await context.sync();

  const targetColumnIndex = side === "right" ? selection.columnIndex + 1 : selection.columnIndex - 1;
  
  // Ensure column index is valid (>= 0)
  if (targetColumnIndex < 0) {
    // Can't insert to the left of column A, insert to the right instead
    const rightCell = context.workbook.worksheets.getActiveWorksheet()
      .getCell(selection.rowIndex, selection.columnIndex + 1);
    rightCell.values = [[text]];
    applyFormatting(rightCell, formatting);
  } else {
    const targetCell = context.workbook.worksheets.getActiveWorksheet()
      .getCell(selection.rowIndex, targetColumnIndex);
    targetCell.values = [[text]];
    applyFormatting(targetCell, formatting);
  }
  
  await context.sync();
}

/**
 * Generic insert with position handling - platform aware
 */
async function insertWithPosition(
  text: string,
  position: InsertPosition,
  formatting?: TextFormatting
): Promise<void> {
  if (!text) return;

  if (isWord()) {
    await insertWithPositionInWord(text, position);
  } else {
    await insertWithPositionInExcel(text, position, formatting);
  }
}

/**
 * Insert text in Excel at the specified position
 */
async function insertWithPositionInExcel(
  text: string,
  position: InsertPosition,
  formatting?: TextFormatting
): Promise<void> {
  try {
    await Excel.run(async (context) => {
      const selection = context.workbook.getSelectedRange();
      const config = getPositionConfig(position);

      if (!config) {
        // Unsupported position - return early
        return;
      }

      if (config.type === "same") {
        selection.load(["rowIndex", "columnIndex"]);
        await context.sync();
        insertAtSameCell(selection, text, formatting);
      } else if (config.type === "cells") {
        selection.load(["rowIndex", "columnIndex", "rowCount"]);
        await context.sync();
        insertBelowSelection(context, selection, text, config.value, formatting);
      } else if (config.type === "column") {
        await insertInColumn(context, selection, text, config.value, formatting);
      }

      await context.sync();
    });
  } catch (error) {
    // Error inserting content in Excel - fail silently
  }
}

/**
 * Insert text in Word at the specified position
 */
async function insertWithPositionInWord(
  text: string,
  position: InsertPosition
): Promise<void> {
  try {
    await Word.run(async (context) => {
      const selection = context.document.getSelection();
      const config = getPositionConfig(position);

      if (!config) {
        // Unsupported position - return early
        return;
      }

      if (config.type === "same") {
        selection.insertText(text, Word.InsertLocation.replace);
      } else if (config.type === "lines") {
        // Move to end of selection and insert newlines + text
        const endRange = selection.getRange(Word.RangeLocation.end);
        const newlines = "\n".repeat(config.value);
        endRange.insertText(newlines + text, Word.InsertLocation.after);
      }
      
      await context.sync();
    });
  } catch (error) {
    // Error inserting content in Word - fail silently
  }
}

/**
 * Insert text content at specified position with optional formatting
 *
 * @param text - Text content to insert
 * @param position - Where to insert (same-cell, N-cells-below, column-left/right)
 * @param formatting - Optional font formatting
 */
export async function insertTextWithPosition(
  text: string,
  position: InsertPosition,
  formatting?: TextFormatting
): Promise<void> {
  return insertWithPosition(text, position, formatting);
}

/**
 * Extract highlighted/selected text from Excel sheet
 */
export async function getHighlightedText(): Promise<string> {
  try {
    if (isWord()) {
      return await Word.run(async (context) => {
        const selection = context.document.getSelection();
        selection.load("text");
        await context.sync();
        return selection.text || "";
      });
    } else {
      return await Excel.run(async (context) => {
        const range = context.workbook.getSelectedRange();
        range.load("values");
        await context.sync();

        const values = range.values;

        if (!values || values.length === 0) {
          return "";
        }

        // Flatten the 2D array of values into a single string
        // (using "\t" to separate columns and "\n" to separate rows)
        const text = values
          .map((row) => row.join("\t"))
          .join("\n")
          .trim();

        return text;
      });
    }
  } catch (error) {
    // Error getting highlighted text - return empty string
    return "";
  }
}

/**
 * Insert values (2D array) below selection
 * @param values - 2D array of values to insert
 * @param rowsBelow - Number of rows to skip before inserting
 */
export async function insertValuesBelowSelection(
  values: any[][],
  rowsBelow: number = 2
): Promise<void> {
  if (!values || values.length === 0) return;

  try {
    await Excel.run(async (context) => {
      const selection = context.workbook.getSelectedRange();
      selection.load(["rowIndex", "columnIndex", "rowCount"]);
      await context.sync();

      const targetRowIndex = selection.rowIndex + selection.rowCount + rowsBelow - 1;
      const targetColumnIndex = selection.columnIndex;

      const targetRange = context.workbook.worksheets.getActiveWorksheet()
        .getRangeByIndexes(
          targetRowIndex,
          targetColumnIndex,
          values.length,
          values[0].length
        );

      targetRange.values = values;
      await context.sync();
    });
  } catch (error) {
    // Error inserting values - fail silently
  }
}
